import { AxiosError } from 'axios';

/**
 * NestJS error body shape. `message` is a string for single errors or a string[]
 * from the global ValidationPipe (one entry per failed constraint).
 */
interface NestErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
}

/** A user-facing message extracted from any error. */
export function extractErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const ax = err as AxiosError<NestErrorBody>;
  const body = ax?.response?.data;
  if (body?.message) {
    return Array.isArray(body.message) ? body.message[0] : body.message;
  }
  if (ax?.message === 'Network Error') {
    return 'Network error — check your connection and try again.';
  }
  return fallback;
}

/** HTTP status of an axios error, or undefined (e.g. network failure). */
export function errorStatus(err: unknown): number | undefined {
  return (err as AxiosError)?.response?.status;
}

/**
 * Best-effort mapping of ValidationPipe messages back onto form fields. NestJS
 * messages are prefixed with the property name (e.g. "email must be an email"),
 * so we match the leading token to a known field key.
 */
export function extractFieldErrors(err: unknown, fieldKeys: string[]): Record<string, string> {
  const ax = err as AxiosError<NestErrorBody>;
  const messages = ax?.response?.data?.message;
  if (!Array.isArray(messages)) return {};

  const result: Record<string, string> = {};
  for (const msg of messages) {
    const firstWord = msg.split(' ')[0];
    const key = fieldKeys.find((k) => k.toLowerCase() === firstWord.toLowerCase());
    if (key && !result[key]) {
      result[key] = msg;
    }
  }
  return result;
}
